import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { articles, type ArticleData } from '@/mocks/articles';

function ArticleSectionRenderer({ section }: { section: ArticleData['sections'][0] }) {
  switch (section.type) {
    case 'heading':
      return (
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5 mt-10">
          {section.content}
        </h2>
      );
    case 'subheading':
      return (
        <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-3 mt-8">
          {section.content}
        </h3>
      );
    case 'text':
      return (
        <p
          className="text-base text-foreground-700 leading-relaxed mb-4"
          dangerouslySetInnerHTML={{ __html: section.content || '' }}
        />
      );
    case 'list':
      return (
        <ul className="space-y-2 mb-5 ml-1">
          {section.items?.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
              <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
              <span dangerouslySetInnerHTML={{ __html: item }} />
            </li>
          ))}
        </ul>
      );
    case 'callout':
      return (
        <div className="bg-primary-50 rounded-lg p-5 md:p-6 my-6 border border-primary-200/50">
          <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-lightbulb-flash-line text-primary-500"></i>
            Key Insight
          </h3>
          <p className="text-sm text-foreground-600 leading-relaxed">{section.content}</p>
        </div>
      );
    case 'comparison':
      return (
        <div className="overflow-x-auto my-6">
          <table className="w-full border-collapse bg-background-100 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-background-200">
                {section.headers?.map((h, i) => (
                  <th key={i} className={`px-4 py-3 text-left text-sm font-semibold ${i === section.headers!.length - 1 ? 'text-primary-700' : 'text-foreground-800'}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/70">
              {section.rows?.map((row, ri) => (
                <tr key={ri} className="hover:bg-background-50/50">
                  {row.map((cell, ci) => (
                    <td key={ci} className={`px-4 py-3 text-sm ${ci === row.length - 1 ? 'text-primary-700 font-medium' : ci === 0 ? 'font-medium text-foreground-800' : 'text-foreground-600'}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'grid':
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
          {section.gridItems?.map((item, i) => (
            <div key={i} className="p-4 rounded-lg bg-background-100 border border-background-200/50">
              <h4 className="font-heading text-base font-semibold text-foreground-950 mb-2">{item.title}</h4>
              <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
}

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <p className="text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors no-underline cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back To Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <title>{article.seoTitle}</title>
      <meta name="description" content={article.metaDescription} />
      <meta name="keywords" content={article.keywords} />
      <link rel="canonical" href={`https://sugargoospread.top/article/${article.slug}`} />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.metaDescription,
          author: { '@type': 'Organization', name: 'Sugargoo Spreadsheet Hub' },
          datePublished: '2026-08-04',
          dateModified: '2026-08-04',
        })}
      </script>
      {article.faqItems.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: article.faqItems.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
          })}
        </script>
      )}

      <div className="min-h-screen flex flex-col bg-background-50">
        <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 no-underline">
              <span className="font-heading text-xl font-semibold text-foreground-950">
                Sugargoo <span className="text-primary-500">Spreadsheet</span>
              </span>
            </Link>
            <div className="flex items-center gap-3 md:gap-4">
              <Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors no-underline flex items-center gap-1 whitespace-nowrap">
                <i className="ri-home-line"></i>
                <span className="hidden sm:inline">Home</span>
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors no-underline cursor-pointer whitespace-nowrap"
              >
                Main Website
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </nav>

        <main className="flex-1">
          <article className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-16">
            <nav className="mb-8 text-xs text-foreground-500" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 flex-wrap">
                <li><Link to="/" className="hover:text-foreground-700 transition-colors no-underline">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-foreground-800 font-medium">{article.title}</li>
              </ol>
            </nav>

            <header className="mb-10">
              <div className="w-full h-56 md:h-72 rounded-lg overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.imageAlt}
                  className="w-full h-full object-cover object-center"
                  title={article.imageAlt}
                />
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  Updated August 4, 2026
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
            </header>

            {article.sections.map((section, i) => (
              <ArticleSectionRenderer key={i} section={section} />
            ))}

            <div className="bg-primary-50 rounded-lg p-6 md:p-8 my-10 text-center border border-primary-200/50">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                Ready To Start Exploring Sugargoo Spreadsheets?
              </h3>
              <p className="text-sm text-foreground-600 mb-6 max-w-lg mx-auto">
                Access thousands of curated products across 14+ categories on the main Sugargoo Spreadsheet platform. Updated daily with the latest finds.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors no-underline cursor-pointer whitespace-nowrap"
                >
                  Visit Main Website
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-background-100 text-foreground-700 text-sm font-semibold hover:bg-background-200 transition-colors no-underline cursor-pointer whitespace-nowrap border border-background-200/70"
                >
                  <i className="ri-home-line"></i>
                  Back To Homepage
                </Link>
              </div>
            </div>

            <nav className="border-t border-background-200/70 pt-8 mt-10">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors no-underline flex items-center gap-1">
                  <i className="ri-arrow-left-line"></i>
                  Back to Sugargoo Spreadsheet Hub
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-primary-600 hover:text-primary-700 transition-colors no-underline flex items-center gap-1"
                >
                  Sugargoo Spreadsheet Main Site
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </nav>
          </article>
        </main>

        <footer className="bg-foreground-950 text-background-100 py-8">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <p className="text-xs text-background-500 mb-2">
              This website provides educational information only. We do not sell products or process payments.
            </p>
            <p className="text-xs text-background-600">
              &copy; {new Date().getFullYear()} Sugargoo Spreadsheet Hub. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}